import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories, getAllTools } from '../data/tools';
import { useLanguageRouter } from '../utils/i18nRouter';
import { SEO } from '../components/SEO';

export const SitemapPage: React.FC = () => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageRouter();
  const allTools = getAllTools();

  // 按分类组织工具
  const toolsByCategory = categories.map(category => ({
    ...category,
    tools: allTools.filter(tool => tool.category === category.id)
  }));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO
        title={t('sitemap.title') + " - " + t('header.title')}
        description={t('sitemap.description')}
        keywords="sitemap, site map, all tools, tool categories, website navigation, toolkitclub pages"
      />
      
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {t('sitemap.title')}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              {t('sitemap.subtitle')}
            </p>
          </div>

          {/* Main Pages */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6 border-b border-gray-200 dark:border-gray-700 pb-2">
              {t('sitemap.mainPages')}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to={getLocalizedPath('/')}
                className="block p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
              >
                <h3 className="font-semibold text-gray-900 dark:text-white">Home</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('sitemap.homeDescription')}
                </p>
              </Link>
              
              <Link
                to={getLocalizedPath('/privacy')}
                className="block p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
              >
                <h3 className="font-semibold text-gray-900 dark:text-white">Privacy Policy</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('sitemap.privacyDescription')}
                </p>
              </Link>
              
              <Link
                to={getLocalizedPath('/terms')}
                className="block p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors"
              >
                <h3 className="font-semibold text-gray-900 dark:text-white">Terms of Service</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('sitemap.termsDescription')}
                </p>
              </Link>
              
              <div className="block p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white">Site Map</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('sitemap.sitemapDescription')}
                </p>
              </div>
            </div>
          </div>

          {/* Tools by Category */}
          <div className="space-y-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
              {t('sitemap.toolCategories')}
            </h2>
            
            {toolsByCategory.map((category) => {
              const IconComponent = category.icon;
              
              return (
                <div key={category.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center mr-3">
                      <IconComponent className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {t(`categories.${category.id}`)}
                    </h3>
                    <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                      ({t('sitemap.toolsInCategory', { count: category.tools.length })})
                    </span>
                  </div>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {category.tools.map((tool) => (
                      <Link
                        key={tool.id}
                        to={getLocalizedPath(tool.path)}
                        className="block p-3 bg-gray-50 dark:bg-gray-700 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                      >
                        <div className="font-medium text-gray-900 dark:text-white text-sm">
                          {t(tool.name)}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                          {t(tool.description)}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Statistics */}
          <div className="mt-12 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-lg p-6 border border-primary-100 dark:border-primary-800/30">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Site Statistics
            </h3>
            <div className="grid md:grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {allTools.length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Total Tools
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {categories.length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Categories
                </div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  18
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Languages
                </div>
              </div>
            </div>
          </div>

          {/* Footer Note */}
          <div className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
            <p>
              All tools are free to use and work directly in your browser. 
              No downloads or registrations required.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
